import { useRequiredContext } from "@redotech/react-util/context";
import ClipboardCheckIcon from "@redotech/redo-icons/clipboard-check.svg";
import ClipboardCheckFilledIcon from "@redotech/redo-icons/clipboard-check_filled.svg";
import ClockRewindIcon from "@redotech/redo-icons/clock-rewind_filled.svg";
import ClockIcon from "@redotech/redo-icons/clock.svg";
import InventoryLevelsIcon from "@redotech/redo-icons/inventory_levels.svg";
import ShoppingBagIcon from "@redotech/redo-icons/shopping-bag-01.svg";
import ShoppingBagFilledIcon from "@redotech/redo-icons/shopping-bag-01_filled.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext } from "react";
import { useLocation } from "react-router-dom";

export function useRedoSuppliesNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useContext(TeamContext);

  const showSuppliesStorefront =
    !!team?.settings?.outboundLabels?.showSuppliesStorefront;
  if (!showSuppliesStorefront) {
    return null;
  }

  const primaryTab: LinkPrimaryTab = {
    id: "redo-supplies",
    hasNotification: false,
    UnfilledIcon: ClipboardCheckIcon,
    FilledIcon: ClipboardCheckFilledIcon,
    type: "link",
    href: routes.redoSuppliesShopHref,
    pathRoot: routes.redoSuppliesBaseUrl,
    friendlyName: "Redo Supplies",
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "redo-supplies",
    name: "Redo Supplies",
    NavContent: SecondaryRedoSuppliesNavigation,
  };

  return { primaryTab, secondaryNavigationData };
}

const RECURRING_PURCHASES_GATED_TEAM_ID = "69a154821c910b9ac2cd4d05";

const SecondaryRedoSuppliesNavigation = memo(
  function SecondaryRedoSuppliesNavigation() {
    const routes = useRequiredContext(MerchantRoutesContext);
    const team = useContext(TeamContext);

    const { id: nodeId, TreeContext } = useNavTreeNode({
      friendlyName: "Redo Supplies",
    });

    const location = useLocation();

    const showRecurringPurchases =
      !!team?.settings?.outboundLabels?.newOmsEnabled &&
      team?._id === RECURRING_PURCHASES_GATED_TEAM_ID;

    const isRecurringPurchasesActive = location.pathname.includes(
      "/recurring-purchases",
    );

    return (
      <TreeContext.Provider value={nodeId}>
        <RedoSecondaryNavItem
          action={{ type: "link", href: routes.redoSuppliesShopHref }}
          FilledIcon={ShoppingBagFilledIcon}
          friendlyName="Shop"
          id="redo-supplies-shop"
          UnfilledIcon={ShoppingBagIcon}
        />
        <RedoSecondaryNavItem
          action={{ type: "link", href: routes.redoSuppliesOrderHistoryHref }}
          FilledIcon={ClockRewindIcon}
          friendlyName="Order history"
          id="redo-supplies-order-history"
          UnfilledIcon={ClockIcon}
        />
        {showRecurringPurchases && (
          <RedoSecondaryNavItem
            action={{ type: "button" }}
            anyChildSelected={isRecurringPurchasesActive}
            FilledIcon={InventoryLevelsIcon}
            friendlyName="Recurring purchases"
            id="redo-supplies-recurring-purchases"
            UnfilledIcon={InventoryLevelsIcon}
          >
            <RedoSecondaryNavItem
              action={{
                type: "link",
                href: routes.redoSuppliesRecurringPurchasesInventoryHref,
              }}
              friendlyName="Inventory-based"
              id="redo-supplies-recurring-inventory"
            />
            <RedoSecondaryNavItem
              action={{
                type: "link",
                href: routes.redoSuppliesRecurringPurchasesTimeHref,
              }}
              friendlyName="Time-based"
              id="redo-supplies-recurring-time"
            />
          </RedoSecondaryNavItem>
        )}
      </TreeContext.Provider>
    );
  },
);
